<template>
  <div ref="mapContainer" class="chart-map-container">
    <div class="chart-wrapper">
      <div ref="chartsDOM" class="chart-content" />
    </div>

    <div class="control-toolbar">
      <div class="toolbar-row-select">
        <el-cascader
          v-model="cascaderOption"
          :options="cascaderOptions"
          :props="{ checkStrictly: true }"
          placeholder="选择地区(支持地区搜索)"
          clearable
          filterable
          @change="onCascaderChange"
        />
      </div>

      <div class="toolbar-row-buttons">
        <button
          :class="['action-btn', { 'btn-active': isLabelShow }]"
          :disabled="!areaCode"
          @click="onToggleLabel"
        >
          {{ isLabelShow ? '隐藏地名' : '显示地名' }}
        </button>

        <button
          class="action-btn"
          :disabled="!areaCode || loading"
          @click="onButtonRefresh"
        >
          <i v-if="loading" class="el-icon-loading" />
          {{ loading ? '刷新中' : '刷新数据' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getAreaCascadeOptions, getChartMapGeojson } from '@/api/geo'

export default {
  name: 'ChartMap',
  data() {
    return {
      myChart: null,
      areaCode: '',
      isLabelShow: false,
      loading: false,
      cascaderOption: [],
      cascaderOptions: []
    }
  },
  mounted() {
    this.getData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.myChart) {
      this.myChart.dispose()
    }
  },
  created() {
    document.title = 'ChartMap'
  },
  methods: {
    getData() {
      getAreaCascadeOptions().then(resp => {
        if (resp.code === 0) {
          this.cascaderOptions = resp.data

          this.cascaderOption = ['900000', '100000']
          this.onCascaderChange(this.cascaderOption)
        }
      })
    },
    onCascaderChange(val) {
      // 兼容清空选择器时的情况
      if (!val || val.length === 0) {
        this.areaCode = ''
        if (this.myChart) this.myChart.clear()
        return
      }

      const length = val.length
      this.areaCode = this.cascaderOption[length - 1]
      this.loading = true

      if (!this.myChart) {
        this.myChart = echarts.init(this.$refs['chartsDOM'])
      }
      this.myChart.showLoading({ text: '地图数据加载中...' })

      getChartMapGeojson(this.areaCode).then(resp => {
        if (resp && resp.code === 0) {
          const { geoJson: geoJsonStr, list: dataList } = resp.data
          const geoJson = typeof geoJsonStr === 'string' ? JSON.parse(geoJsonStr) : geoJsonStr
          const mapName = this.areaCode
          const splitList = [
            { start: 800, end: 1000 },
            { start: 600, end: 800 },
            { start: 400, end: 600 },
            { start: 200, end: 400 },
            { start: 100, end: 200 },
            { start: 0, end: 100 }
          ]
          const chartOption = this.buildChartOption(mapName, dataList, splitList)

          echarts.registerMap(mapName, geoJson)
          this.myChart.setOption(chartOption, true)
        }
      }).catch(err => {
        this.$message.error('加载地图数据失败')
        console.error(err)
      }).finally(() => {
        this.loading = false
        if (this.myChart) {
          this.myChart.hideLoading()
        }
      })
    },
    buildChartOption(mapName, dataList, splitList) {
      return {
        backgroundColor: '#FFFFFF',
        title: {
          text: '地区数据统计地图',
          subtext: '数据实时更新',
          left: 'center',
          top: 20
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}<br/>{a}: {c}'
        },
        visualMap: {
          show: true,
          left: 30,
          bottom: 30,
          splitList: splitList,
          // --- 核心修改：红色系由深到浅渐变 ---
          // 对应 6 个分段：数值最高(深红) -> 数值最低(极浅红/粉白)
          color: [
            '#cf1322', // 强烈的深红色（对应最高区间，如 800-1000）
            '#f5222d', // 标准大红色
            '#ff4d4f', // 亮红色
            '#ff7875', // 珊瑚红 / 浅红
            '#ffa39e', // 粉红色
            '#fff1f0' // 极浅的粉白（对应最低区间，如 0-100）
          ]
        },
        series: [{
          name: '数量',
          type: 'map',
          map: mapName,
          roam: true,
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1
          },
          emphasis: {
            itemStyle: {
              areaColor: '#389e0d'
            }
          },
          label: {
            show: this.isLabelShow,
            color: '#333',
            fontSize: 11
          },
          data: dataList
        }]
      }
    },
    onToggleLabel() {
      if (!this.myChart) return
      this.isLabelShow = !this.isLabelShow
      this.myChart.setOption({
        series: [{
          label: {
            show: this.isLabelShow
          }
        }]
      })
    },
    onButtonRefresh() {
      // 核心修正：这里原先调用了闲置的 onSelect()，已修正为直接调用事件处理
      if (this.cascaderOption.length > 0) {
        this.onCascaderChange(this.cascaderOption)
      } else {
        this.$message.warning('请先选择地区再进行数据刷新')
      }
    },
    handleResize() {
      if (this.myChart) {
        this.myChart.resize()
      }
    }
  }
}
</script>

<style scoped>
.chart-map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
}

.chart-content {
  width: 100%;
  height: 100%;
}

/* ================= UI 优化核心样式 ================= */

/* 右上角工具箱容器：将总宽度调整至 220px 适配更宽的级联选择器 */
.control-toolbar {
  position: absolute;
  z-index: 999;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px; /* 稍微内缩 padding 显得更精致 */
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
  width: 220px; /* 👈 从 160px 拓宽至 220px */

  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 选择器行占满宽度 */
.toolbar-row-select {
  width: 100%;
}

/* 确保 el-cascader 内部完全铺满行宽 */
.toolbar-row-select .el-cascader {
  width: 100%;
}

/* 按钮行依旧平分 */
.toolbar-row-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}

/* 通用轻量按钮样式 */
.action-btn {
  height: 28px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  color: #606266;
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0 4px;
  white-space: nowrap;
}

.action-btn:hover:not(:disabled) {
  color: #fc5531;
  border-color: #fc5531;
  background-color: rgba(252, 86, 49, 0.05);
}

.action-btn:disabled {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}

.btn-active {
  background-color: #fc5531 !important;
  color: #fff !important;
  border-color: #fc5531 !important;
}

/* 深度选择器穿透：彻底重构为适配 el-cascader 的紧凑型地图外观 */
::v-deep .el-cascader .el-input__inner {
  height: 28px !important;
  line-height: 28px !important;
  font-size: 11px;
  border-color: #dcdfe6;
  padding-left: 8px; /* 轻微缩进提升留白美感 */
}

::v-deep .el-cascader .el-input__icon {
  line-height: 28px !important;
}
</style>
